import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Heart } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import { useAuthStore } from '../stores/authStore';
import { useFavoritesStore } from '../stores/favoritesStore';

export default function MyFavoritesPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const favorites = useFavoritesStore((state) => state.favorites);
  const loading = useFavoritesStore((state) => state.loading);
  const loaded = useFavoritesStore((state) => state.loaded);
  const fetchFavorites = useFavoritesStore((state) => state.fetchFavorites);
  const toggleFavorite = useFavoritesStore((state) => state.toggleFavorite);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchFavorites();
  }, [user, navigate, fetchFavorites]);

  const handleRemove = async (classId: string, classTitle: string) => {
    const confirmed = confirm(`"${classTitle}" 수업을 관심 수업에서 제거하시겠습니까?`);
    if (!confirmed) return;

    setRemovingId(classId);
    try {
      await toggleFavorite(classId);
    } catch (error: any) {
      alert(error?.message || '관심 수업 해제에 실패했습니다.');
    } finally {
      setRemovingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SCHEDULED':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">모집예정</span>;
      case 'RECRUITING':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">모집중</span>;
      case 'IN_PROGRESS':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">진행중</span>;
      case 'COMPLETED':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">종료</span>;
      case 'CLOSED':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">마감</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading && !loaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
          <AdminSidebar />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">로딩 중...</p>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />
        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">관심 수업</h1>
              <p className="text-gray-600">관심 표시한 수업 목록입니다. 카드를 누르면 상세로 이동합니다.</p>
            </div>

            {favorites.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">관심 표시한 수업이 없습니다</h3>
                <p className="text-gray-600 mb-6">수업 상세 페이지에서 ❤️ 버튼을 눌러 저장해 보세요.</p>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  수업 둘러보기
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {favorites.map((favorite) => {
                  const classData = favorite.class;
                  if (!classData) return null;

                  return (
                    <div
                      key={favorite.id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row">
                        <div
                          className="w-full md:w-64 h-48 md:h-auto bg-gray-200 cursor-pointer"
                          onClick={() => navigate(`/classes/${classData.id}`)}
                        >
                          {classData.thumbnail_url ? (
                            <img
                              src={classData.thumbnail_url}
                              alt={classData.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              이미지 없음
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {getStatusBadge(classData.status)}
                              </div>
                              <h3
                                className="text-xl font-bold text-gray-900 mb-2 cursor-pointer hover:text-primary transition-colors"
                                onClick={() => navigate(`/classes/${classData.id}`)}
                              >
                                {classData.title}
                              </h3>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>
                                    {formatDate(classData.start_date)} - {formatDate(classData.end_date)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() => handleRemove(classData.id, classData.title)}
                              disabled={removingId === classData.id}
                              className={`ml-4 px-4 py-2 rounded-lg border border-gray-200 hover:border-accent text-gray-700 hover:text-accent transition-colors flex items-center gap-2 ${
                                removingId === classData.id ? 'opacity-50 cursor-wait' : ''
                              }`}
                            >
                              <Heart className="w-4 h-4 fill-accent text-accent" />
                              {removingId === classData.id ? '해제 중...' : '관심 해제'}
                            </button>
                          </div>

                          <div className="pt-4 border-t border-gray-100">
                            <p className="text-sm text-gray-500">
                              저장일: {formatDate(favorite.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
